import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AssetsPicker from "./AssetsPicker";

interface Props {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  testId?: string;
  /**
   * Render as an image thumbnail when a value is set, hiding the URL. Useful
   * for background and logo fields where the URL string isn't useful info.
   */
  preview?: boolean;
}

export default function AssetField({
  label,
  value,
  onChange,
  placeholder,
  testId,
  preview = false,
}: Props) {
  const { t } = useTranslation(["content", "common"]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Preview mode + value set: show a thumbnail with Change/Clear actions.
  if (preview && value) {
    return (
      <>
        {label && (
          <Typography
            variant="body2"
            sx={{ fontWeight: "fontWeightMedium", mb: 0.5 }}
          >
            {label}
          </Typography>
        )}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 96,
              height: 54,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              backgroundImage: `url(${value})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              bgcolor: "grey.100",
              flexShrink: 0,
            }}
            data-testid={testId ? `${testId}-preview` : "asset-preview"}
          />
          <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPickerOpen(true)}
              data-testid={testId ? `${testId}-browse` : "asset-browse"}
            >
              {t("assetField.change")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() => onChange("")}
              data-testid={testId ? `${testId}-clear` : "asset-clear"}
            >
              {t("common:buttons.remove")}
            </Button>
          </Stack>
        </Stack>
        <AssetsPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => onChange(url)}
        />
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="stretch">
        <TextField
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          fullWidth
          inputProps={testId ? { "data-testid": testId } : undefined}
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <ImageIcon fontSize="small" color="action" />
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Button
          variant="outlined"
          onClick={() => setPickerOpen(true)}
          data-testid={testId ? `${testId}-browse` : "asset-browse"}
          sx={{ minWidth: 96, whiteSpace: "nowrap" }}
        >
          {t("assetField.browse")}
        </Button>
      </Stack>
      <AssetsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => onChange(url)}
      />
    </>
  );
}
